/**
 * No-login Preview page — fully isolated from the authenticated incident
 * intake flow. Lives at /preview.
 *
 * Lifecycle:
 *  1. Generate (or read) an `anonymous_trial_id` from localStorage.
 *  2. GET /api/preview/quota → if blocked, show the "already used" view.
 *  3. POST /api/preview/start to record that the visitor opened the page.
 *  4. On "Dispatch Agents" click, POST /api/preview/analyze and navigate
 *     to /preview/{incident_id}.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Send, ShieldCheck, X, Sparkles, LogIn } from "lucide-react";
import Header from "@/components/cre/Header";
import Footer from "@/components/cre/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  getOrCreateAnonymousTrialId,
  fetchPreviewQuota,
  startPreviewSession,
  runPreviewAnalysis,
} from "@/lib/previewClient";

export default function Preview() {
  const navigate = useNavigate();
  const trialId = useMemo(() => getOrCreateAnonymousTrialId(), []);
  const [quota, setQuota] = useState(null);
  const [busy, setBusy] = useState(false);
  const [anonConfirmed, setAnonConfirmed] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    severity: "P2",
    affected_system: "",
    symptoms: [""],
    telemetry_hints: "",
  });

  // Lifecycle: check quota, then mark started if not blocked.
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const q = await fetchPreviewQuota(trialId);
        if (!mounted) return;
        setQuota(q);
        if (!q.blocked) {
          await startPreviewSession(trialId);
        }
      } catch (e) {
        // Non-fatal — if quota check fails the analyze call will still
        // block on the server side.
      }
    })();
    return () => {
      mounted = false;
    };
  }, [trialId]);

  const updateSymptom = (i, v) =>
    setForm((f) => ({
      ...f,
      symptoms: f.symptoms.map((s, idx) => (idx === i ? v : s)),
    }));

  const submit = async () => {
    if (!form.title.trim() || !form.description.trim() || !form.affected_system.trim()) {
      toast.error("Title, description, and affected system are required");
      return;
    }
    if (!anonConfirmed) {
      toast.error(
        "Please confirm the inputs are anonymized before continuing."
      );
      return;
    }
    if (quota?.blocked) {
      toast.error(
        "You have already used your no-login preview. Sign in free to get 3 full CRE AI Ops analyses."
      );
      return;
    }
    setBusy(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        severity: form.severity,
        affected_system: form.affected_system.trim(),
        symptoms: form.symptoms.map((s) => s.trim()).filter(Boolean),
        telemetry_hints: form.telemetry_hints?.trim() || null,
      };
      const inc = await runPreviewAnalysis(trialId, payload);
      toast.success("Preview analysis started");
      navigate(`/preview/${inc.id}`);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : null;
      if (code === "anonymous_preview_used" || status === 402) {
        setQuota({ used: 1, limit: 1, remaining: 0, blocked: true });
        toast.error(
          detail?.message ||
            "You have already used your no-login preview. Sign in free to get 3 full CRE AI Ops analyses."
        );
      } else {
        toast.error(
          (typeof detail === "string" && detail) ||
            detail?.message ||
            "Failed to start preview analysis."
        );
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 text-slate-900">
      <Header />
      <main
        data-testid="preview-page"
        className="flex-1 max-w-3xl w-full mx-auto px-5 md:px-8 pt-7 md:pt-10 pb-12 md:pb-16"
      >
        <Link
          to="/"
          data-testid="preview-back-home"
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-sky-700 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={1.75} />
          Back to Home
        </Link>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-sky-200 bg-sky-50 text-sky-700 text-[11.5px] font-semibold">
            <Sparkles className="h-3 w-3" strokeWidth={2.25} />
            No-login preview
          </div>
          {quota?.admin_bypass ? (
            <div
              data-testid="preview-admin-bypass-pill"
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-violet-200 bg-violet-50 text-violet-700 text-[11.5px] font-semibold"
            >
              <ShieldCheck className="h-3 w-3" strokeWidth={2.25} />
              Admin preview testing mode enabled
            </div>
          ) : null}
        </div>

        <h1
          data-testid="preview-heading"
          className="mt-3 font-heading font-black tracking-tight text-slate-900 text-3xl sm:text-4xl lg:text-5xl leading-[1.06]"
        >
          Try CRE without login
        </h1>

        <p className="mt-3 text-sm sm:text-base text-slate-600 leading-relaxed max-w-2xl">
          Enter an anonymized incident scenario below and dispatch the CRE
          reasoning agents once. Preview output is limited — sign in free to
          get 3 full analyses with saved history, PDF export, share cards,
          and approval-ready runbooks.
        </p>

        {/* Quota state — blocked banner */}
        {quota?.blocked ? (
          <div
            data-testid="preview-blocked-banner"
            className="mt-5 rounded-xl border border-rose-200 bg-rose-50 p-4 flex flex-wrap gap-3 items-start"
          >
            <X className="h-4 w-4 text-rose-500 shrink-0 mt-0.5" strokeWidth={2} />
            <div className="flex-1 min-w-[220px]">
              <p
                data-testid="preview-blocked-message"
                className="text-[13px] sm:text-sm text-rose-800 leading-relaxed"
              >
                You have already used your no-login preview.{" "}
                <span className="font-semibold">
                  Sign in free to get 3 full CRE AI Ops analyses.
                </span>
              </p>
            </div>
            <Link to="/register" data-testid="preview-blocked-cta">
              <Button
                size="sm"
                className="rounded-lg bg-rose-600 hover:bg-rose-500 text-white font-semibold"
              >
                Create free account
                <LogIn className="ml-1.5 h-3.5 w-3.5" strokeWidth={2} />
              </Button>
            </Link>
          </div>
        ) : null}

        {/* Anonymization notice */}
        <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4 flex gap-3">
          <ShieldCheck className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" strokeWidth={2} />
          <p className="text-[12.5px] sm:text-[13px] text-slate-700 leading-relaxed">
            Enter anonymized incident details only. Do not include passwords,
            secrets, API keys, customer data, or confidential production logs.
          </p>
        </div>

        {/* Form */}
        <fieldset
          disabled={quota?.blocked || busy}
          className={`mt-6 space-y-4 ${quota?.blocked ? "opacity-60 pointer-events-none" : ""}`}
        >
          <div>
            <Label htmlFor="title" className="text-slate-700">Title</Label>
            <Input
              id="title"
              data-testid="preview-title"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="One-line description of the incident"
              className="mt-1.5"
            />
          </div>

          <div>
            <Label htmlFor="description" className="text-slate-700">Description</Label>
            <Textarea
              id="description"
              data-testid="preview-description"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="What happened, when, and what telemetry was observed?"
              rows={5}
              className="mt-1.5"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="severity" className="text-slate-700">Severity</Label>
              <Select
                value={form.severity}
                onValueChange={(v) => setForm((f) => ({ ...f, severity: v }))}
              >
                <SelectTrigger className="mt-1.5" data-testid="preview-severity">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="P1">P1 — Customer-visible outage</SelectItem>
                  <SelectItem value="P2">P2 — Major degradation</SelectItem>
                  <SelectItem value="P3">P3 — Partial degradation</SelectItem>
                  <SelectItem value="P4">P4 — Minor / observability</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="affected" className="text-slate-700">Affected system</Label>
              <Input
                id="affected"
                data-testid="preview-affected"
                value={form.affected_system}
                onChange={(e) =>
                  setForm((f) => ({ ...f, affected_system: e.target.value }))
                }
                placeholder="checkout-api (prod, us-east-1)"
                className="mt-1.5"
              />
            </div>
          </div>

          <div>
            <Label className="text-slate-700">Symptoms</Label>
            <div className="mt-1.5 space-y-2">
              {form.symptoms.map((s, i) => (
                <Input
                  key={i}
                  value={s}
                  onChange={(e) => updateSymptom(i, e.target.value)}
                  placeholder="e.g. p99 latency 9s, 5xx 12%"
                  data-testid={`preview-symptom-${i}`}
                />
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setForm((f) => ({ ...f, symptoms: [...f.symptoms, ""] }))}
                className="rounded-lg border-slate-200 text-slate-700 hover:bg-slate-100 hover:text-slate-900 font-semibold"
              >
                Add symptom
              </Button>
            </div>
          </div>

          <div>
            <Label htmlFor="telemetry" className="text-slate-700">
              Telemetry hints (optional)
            </Label>
            <Textarea
              id="telemetry"
              data-testid="preview-telemetry"
              value={form.telemetry_hints}
              onChange={(e) =>
                setForm((f) => ({ ...f, telemetry_hints: e.target.value }))
              }
              placeholder="Dashboards, traces, or correlated deploy IDs"
              rows={2}
              className="mt-1.5"
            />
          </div>

          <label className="flex items-start gap-2.5 cursor-pointer">
            <Checkbox
              checked={anonConfirmed}
              onCheckedChange={(v) => setAnonConfirmed(Boolean(v))}
              className="mt-0.5"
              data-testid="preview-anon-confirm"
            />
            <span className="text-[12.5px] text-slate-600 leading-relaxed">
              I confirm the incident details above are anonymized — no secrets,
              credentials, customer data, or confidential production logs.
            </span>
          </label>

          <Button
            type="button"
            onClick={submit}
            disabled={busy || quota?.blocked}
            data-testid="preview-dispatch"
            size="lg"
            className="cre-cta-primary rounded-lg font-semibold px-5 h-11 mt-1"
          >
            {busy ? "Dispatching…" : "Dispatch Agents"}
            <Send className="ml-2 h-4 w-4" strokeWidth={2} />
          </Button>
          <p className="text-[12px] text-slate-500">
            Preview is consumed only after you dispatch. One preview per
            browser.
          </p>
        </fieldset>
      </main>
      <Footer />
    </div>
  );
}
